import { useEffect, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, TextField } from '@mui/material';
import Grid from '@mui/material/Grid';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { handleError } from 'src/redux-store/slices/errorSlice';
import LoadingOverlay from 'src/components/common/LoadingOverlay';

// Define TypeScript interfaces
interface FeeData {
  [key: string]: number;
}

interface FeeGroup {
  label: string;
  data: FeeData;
}

// Define two possible structures for our fee data
interface FlatFeeStructure {
  ID?: number | null;
  [key: string]: number | null | undefined;
}

interface NestedFeeStructure {
  ID?: number | null;
  [category: string]: FeeData | number | null | undefined;
}

export default function UCCFeeConfiguration({ UCCFee, getUCCId }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<string | false>(false);
  // Use union type to handle both flat and nested structures
  const [finalFee, setFinalFee] = useState<FlatFeeStructure | NestedFeeStructure>({});

  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  // Helper function to convert flat data to structured format
  const structureData = (flatData: FlatFeeStructure): NestedFeeStructure => {
    const structuredFees: NestedFeeStructure = {
      ID: flatData?.ID
    };

    UCCFee?.forEach((group) => {
      const categoryKey = group.label;
      structuredFees[categoryKey] = {} as FeeData;

      Object.keys(group.data).forEach((key) => {
        if (flatData[key] !== undefined && structuredFees[categoryKey] && typeof structuredFees[categoryKey] === 'object') {
          (structuredFees[categoryKey] as FeeData)[key] = flatData[key] as number;
        }
      });
    });

    return structuredFees;
  };

  // Helper function to flatten structured data
  // const flattenData = (structuredData: NestedFeeStructure): FlatFeeStructure => {
  //   const flatData: FlatFeeStructure = {
  //     ID: structuredData.ID
  //   };

  //   UCCFee.forEach((group) => {
  //     const categoryKey = group.label;
  //     const categoryData = structuredData[categoryKey];

  //     if (categoryData && typeof categoryData === 'object') {
  //       Object.entries(categoryData).forEach(([key, value]) => {
  //         flatData[key] = value;
  //       });
  //     }
  //   });

  //   return flatData;
  // };

  const getData = async (): Promise<void> => {
    setLoading(true);

    try {
      const response = await httpRequest(
        'GET',
        `/construction/get_fees/${getUCCId}`
      );
      if (response?.status === 'success') {
        const data1 = response?.data;

        if (getUCCId === 1) {
          const data = data1?.FeeStructure || {} as FlatFeeStructure;
          // For UCC ID 1, check if the response is already structured or flat
          if (data?.VolumeFees || data?.MinimumFees || data?.Certificates || data?.AdministrativeFees || data?.RenovationFees || data?.InstallationFees) {
            // Data is already structured
            setFinalFee({ ...data, ID: data1?.ID || null });
          } else {
            // Convert flat API response to structured format
            const structuredFees = structureData(data);
            setFinalFee(structuredFees);
          }
        } else {
          // For other IDs, keep flat structure as before
          const allFees: FlatFeeStructure = {};
          UCCFee.forEach((group) => {
            Object.entries(group.data).forEach(([key]) => {
              allFees[key] = data1[key] || 0;
            });
          });
          allFees.ID = data1?.ID;
          setFinalFee(allFees);
        }
        
        setLoading(false);
      } else {
        toast.error(response?.message || 'An error occurred');
        setLoading(false);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred while fetching permits';
      dispatch(handleError(errorMessage));
      toast.error('An error occurred while fetching permits');
      setLoading(false);
    }
  };

  // Initialize all fee fields on mount
  useEffect(() => {
    if (getUCCId === 1) {
      // Initialize with structured categories for getUCCId === 1
      const structuredFees: NestedFeeStructure = {};

      UCCFee?.forEach((group) => {
        const categoryKey = group.label;
        structuredFees[categoryKey] = {} as FeeData;

        Object.entries(group.data).forEach(([key, value]) => {
          if (structuredFees[categoryKey] && typeof structuredFees[categoryKey] === 'object') {
            (structuredFees[categoryKey] as FeeData)[key] = value as number;
          }
        });
      });

      structuredFees.ID = null;
      setFinalFee(structuredFees);
    } else {
      // Initialize with flat structure for other IDs
      const allFees: FlatFeeStructure = {};
      UCCFee?.forEach((group) => {
        Object.entries(group.data).forEach(([key, value]) => {
          allFees[key] = value as number;
        });
      });
      allFees.ID = null;
      setFinalFee(allFees);
    }

    getData();
  }, [UCCFee, getUCCId]);

  const handleInputChange = (fieldName: string, value: number, category: string | null = null): void => {
    if (getUCCId === 1 && category) {
      // For structured data, update nested objects
      setFinalFee((prev) => {
        const updatedFees = { ...prev } as NestedFeeStructure;
        if (!updatedFees[category]) {
          updatedFees[category] = {};
        }
        if (updatedFees[category] && typeof updatedFees[category] === 'object') {
          (updatedFees[category] as FeeData)[fieldName] = value;
        }
        return updatedFees;
      });
    } else {
      // For flat structure
      setFinalFee((prev) => ({
        ...prev,
        [fieldName]: value
      }));
    }
  };

  // Create payload according to UCC ID
  const formatRequestData = (): FlatFeeStructure | NestedFeeStructure => {
    if (getUCCId === 1) {
      // Ensure the structured data is properly populated
      const structuredData = finalFee as NestedFeeStructure;

      // Verify all categories have values
      UCCFee.forEach((group) => {
        const categoryKey = group.label;
        if (!structuredData[categoryKey] || typeof structuredData[categoryKey] !== 'object') {
          structuredData[categoryKey] = {} as FeeData;
        }

        // Ensure all fields have values
        Object.keys(group.data).forEach((key) => {
          if (structuredData[categoryKey] && typeof structuredData[categoryKey] === 'object') {
            const typedCategory = structuredData[categoryKey] as FeeData;
            if (typedCategory[key] === undefined) {
              typedCategory[key] = 0;
            }
          }
        });
      });

      return structuredData;
    } else {
      // Return flat format for other IDs
      return finalFee as FlatFeeStructure;
    }
  };

  const handleSubmit = async (): Promise<void> => {
    setLoading(true);

    try {
      const requestData = formatRequestData();

      const feeId = getUCCId === 1
        ? (requestData as NestedFeeStructure).ID
        : (requestData as FlatFeeStructure).ID;

      // Prepare API endpoint
      const endpoint = `/construction/update_fees/subcode/${getUCCId}/fees/${feeId}`;

      // Prepare payload based on UCC ID
      let payload;
      if (getUCCId === 1) {
        const newPayload = {
          ID: requestData.ID,
          FeeStructure: {
            AdministrativeFees: requestData.AdministrativeFees,
            Certificates: requestData.Certificates,
            InstallationFees: requestData.InstallationFees,
            MinimumFees: requestData.MinimumFees,
            RenovationFees: requestData.RenovationFees,
            VolumeFees: requestData.VolumeFees 
          }
        }
        // Keep structured format for UCC ID 1
        payload = newPayload;
      } else {
        // Keep flat format for other IDs
        payload = requestData;
      }

      const response = await httpRequest('PUT', endpoint, payload);

      if (response?.status === 'success') {
        toast.success(`Fee updated successfully.`);
        getData();
      } else {
        toast.error(response?.message || 'An error occurred');
        dispatch(handleError(response?.message || 'Unknown error'));
        setLoading(false);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred while updating fees';
      toast.error('An error occurred while updating fees');
      dispatch(handleError(errorMessage));
      setLoading(false);
    }
  };

  // Helper function to format labels
  const formatLabel = (text: string): string => {
    return text.replace(/([A-Z])/g, ' $1').replace(/(\d+)/g, ' $1').trim();
  };

  const renderTextField = (key: string, value: number, category: string | null = null): JSX.Element => {
    return (
      <TextField
        fullWidth
        label={formatLabel(key)}
        value={value ?? 0}
        onChange={(e) => handleInputChange(
          key,
          Number(e.target.value),
          category
        )}
        type="number"
        inputProps={{
          min: 0,
          step: 0.01
        }}
      />
    );
  };

  // Helper function to safely get fee value from either structure
  const getFeeValue = (group: FeeGroup, key: string): number => {
    if (getUCCId === 1) {
      const nestedFees = finalFee as NestedFeeStructure;
      return nestedFees[group.label] &&
        typeof nestedFees[group.label] === 'object' ?
        ((nestedFees[group.label] as FeeData)[key] ?? 0) : 0;
    } else {
      return ((finalFee as FlatFeeStructure)[key] ?? 0) as number;
    }
  };

  return (
    <div>
      {loading && <LoadingOverlay />}

      {UCCFee.map((feeGroup, index) => {
        const panelId = `panel${index}`;
        return (
          <Accordion
            key={index}
            expanded={expanded === panelId}
            onChange={handleChange(panelId)}
            sx={{ marginY: 2, backgroundColor: '#f4f4f4' }}
          >
            <AccordionSummary
              expandIcon={<ArrowDropDownIcon />}
              aria-controls={`${panelId}-content`}
              id={`${panelId}-header`}
            >
              <Typography variant="h5">{formatLabel(feeGroup.label)}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {Object.entries(feeGroup.data).map(([key]) => (
                  <Grid key={key} item xs={12} sm={6} md={3} lg={2}>
                    {getUCCId === 1 ?
                      renderTextField(key, getFeeValue(feeGroup, key), feeGroup.label) :
                      renderTextField(key, getFeeValue(feeGroup, key))
                    }
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Button
        variant="contained"
        sx={{ mt: 2 }}
        onClick={handleSubmit}
        disabled={loading}
      >
        Save
      </Button>
    </div>
  );
}